import { Button, ButtonSize, ButtonVariant, Select } from "shared/components";
import { Card } from "shared/components/Card";
import {
  hasValidationError,
  selectActiveCondition,
  selectSortedQuestions,
  useSurveyStore
} from "../../../../store";
import { InputSize } from "shared/components/forms/Input";
import { useMemo } from "react";
import { ConditionHeader } from "../../components/header";
import { VisualConnector } from "../../components/visual-connector";
import {
  ACTION_OPTIONS,
  FIELD_QUESTION_TYPES,
  getAnswerOptions,
  getConditionTypeOptions,
  hasAnswerSelection
} from "../../constants";

export const ConditionEdit = () => {
  const activeCondition = useSurveyStore(selectActiveCondition);
  const activeConditionIdx = useSurveyStore(
    (state) => state.activeConditionIdx
  );
  const updateActiveCondition = useSurveyStore(
    (state) => state.updateActiveCondition
  );
  const updateActiveConditionIdx = useSurveyStore(
    (state) => state.updateActiveConditionIdx
  );
  const questions = useSurveyStore(selectSortedQuestions);
  const validationErrors = useSurveyStore((state) => state.validationErrors);
  const idx = activeConditionIdx ?? 0;
  const fieldError = (field: Parameters<typeof hasValidationError>[3]) =>
    hasValidationError(validationErrors, "condition", idx, field);

  const questionOptions = useMemo(
    () =>
      questions.map((q) => ({
        label: `${q.index + 1}. ${q.question || "(untitled)"}`,
        value: q.internal_id
      })),
    [questions]
  );

  const parentQuestion = questions.find(
    (q) => q.internal_id === activeCondition?.parent_question_internal_id
  );

  const conditionTypeOptions = getConditionTypeOptions(parentQuestion);
  const answerOptions = getAnswerOptions(parentQuestion);
  const showAnswerSelect = hasAnswerSelection(parentQuestion);

  const childQuestionOptions = useMemo(() => {
    const parentIdx = parentQuestion?.index ?? -1;
    return questions
      .filter(
        (q) =>
          q.index > parentIdx &&
          q.internal_id !== activeCondition?.parent_question_internal_id
      )
      .map((q) => ({
        label: `${q.index + 1}. ${q.question || "(untitled)"}`,
        value: q.internal_id
      }));
  }, [
    questions,
    activeCondition?.parent_question_internal_id,
    parentQuestion?.index
  ]);

  if (!activeCondition) return null;

  const conditionType =
    (activeCondition.condition.type as string) ||
    conditionTypeOptions[0]?.value ||
    "";

  const conditionAnswer = (activeCondition.condition.answer as string) || "";

  return (
    <div className="w-full flex flex-col min-h-0 h-full">
      <ConditionHeader
        editMode
        onBack={() => updateActiveConditionIdx(null)}
        onAdd={() => {}}
      />
      <div className="py-6 px-4 overflow-y-auto flex-1">
        <div className="max-w-[800px] mx-auto flex flex-col gap-0">
          <Card className="p-4 !bg-background-900">
            {/* IF section */}
            <Card as="section" className="p-5">
              <h3 className="text-base font-bold text-content-1000 mb-5 border-b border-alpha-40 pb-5">
                Condition {(activeConditionIdx ?? 0) + 1}
              </h3>

              <div className="flex items-start gap-3 w-full">
                <span className="text-primary-500 font-bold text-sm w-8 shrink-0">
                  if
                </span>

                <div className="flex flex-col gap-3 flex-1">
                  <Select
                    id="condition-parent-question"
                    placeholder="Select question"
                    size={InputSize.SM}
                    hideOptional
                    legacyErrorMargins={false}
                    error={fieldError("parent_question")}
                    value={
                      activeCondition.parent_question_internal_id || undefined
                    }
                    options={questionOptions}
                    onChange={(e) => {
                      const parentId = e.target.value;
                      const newParent = questions.find(
                        (q) => q.internal_id === parentId
                      );
                      const isFieldType = newParent
                        ? FIELD_QUESTION_TYPES.has(newParent.type)
                        : false;

                      updateActiveCondition((prev) => ({
                        ...prev,
                        parent_question_internal_id: parentId,
                        child_question_internal_ids: [],
                        condition: {
                          type: isFieldType ? "filled_in" : "selected"
                        }
                      }));
                    }}
                  />

                  <div className="grid grid-cols-[140px_1fr] gap-3">
                    <Select
                      id="condition-type"
                      size={InputSize.SM}
                      hideOptional
                      legacyErrorMargins={false}
                      error={fieldError("condition_type")}
                      value={conditionType}
                      options={conditionTypeOptions}
                      onChange={(e) => {
                        updateActiveCondition((prev) => ({
                          ...prev,
                          condition: { type: e.target.value }
                        }));
                      }}
                    />

                    {showAnswerSelect ? (
                      <Select
                        id="condition-answer"
                        placeholder="Select answer"
                        size={InputSize.SM}
                        hideOptional
                        legacyErrorMargins={false}
                        error={fieldError("answer")}
                        value={conditionAnswer || undefined}
                        options={answerOptions}
                        onChange={(e) => {
                          updateActiveCondition((prev) => ({
                            ...prev,
                            condition: {
                              ...prev.condition,
                              answer: e.target.value
                            }
                          }));
                        }}
                      />
                    ) : (
                      <Select
                        id="condition-answer-disabled"
                        placeholder="Select answer"
                        size={InputSize.SM}
                        hideOptional
                        legacyErrorMargins={false}
                        isDisabled
                        options={[]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <VisualConnector />

            {/* THEN section */}
            <Card as="section" className="p-5">
              <div className="flex items-start gap-3 w-full">
                <span className="text-primary-500 font-bold text-sm w-8 shrink-0">
                  then
                </span>

                <div className="grid grid-cols-[140px_1fr] gap-3 flex-1 min-w-0">
                  <Select
                    id="condition-action"
                    placeholder="Select"
                    size={InputSize.SM}
                    hideOptional
                    legacyErrorMargins={false}
                    error={fieldError("action")}
                    value={activeCondition.action || undefined}
                    options={ACTION_OPTIONS}
                    onChange={(e) => {
                      const action = e.target.value;
                      updateActiveCondition((prev) => ({
                        ...prev,
                        action,
                        ...(action === "end_survey"
                          ? { child_question_internal_ids: [] }
                          : {})
                      }));
                    }}
                  />

                  {activeCondition.action !== "end_survey" && (
                    <Select
                      id="condition-child-questions"
                      placeholder="Select question"
                      size={InputSize.SM}
                      hideOptional
                      legacyErrorMargins={false}
                      error={fieldError("child_questions")}
                      isMulti
                      value={activeCondition.child_question_internal_ids}
                      options={childQuestionOptions}
                      onChange={(e) => {
                        const value = e.target.value;
                        const ids =
                          typeof value === "string"
                            ? [value]
                            : (value as string[]);
                        updateActiveCondition((prev) => ({
                          ...prev,
                          child_question_internal_ids: ids
                        }));
                      }}
                    />
                  )}
                </div>
              </div>
            </Card>

            <div className="flex justify-end mt-3">
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.PRIMARY}
                onClick={() => updateActiveConditionIdx(null)}
              >
                Save and apply
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
